
import NavigationTabBar from '@/layouts/components/NavigationTabBar.vue'
import NavigationTabBarItem from '@/layouts/components/NavigationTabBarItem.vue'
import {
  computed,
  defineComponent, onMounted,
  reactive,
  ref,
  toRefs, watch,
} from 'vue'
import { useStore } from 'vuex'
import { useQuasar } from 'quasar'
import useContentModule, { Mutation } from '@/pages/composables/useContentModule'
import { CtCatalogue, CtCatalogueProduct } from '@/models/interfaces/content-type'
import AddIcon from '@/components/Icons/AddIcon.vue'
import ChannelDetails from '@/pages/Channels/components/ChannelDetails.vue'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import { NavigationFailureType, isNavigationFailure, useRoute, useRouter } from 'vue-router'
import DButton from '@/components/base/DButton.vue'
import DButtonToggle from '@/components/base/DButtonToggle.vue'
import { cloneDeep } from 'lodash'

import SelectHeader from '@/components/SelectHeader.vue'
import ProductCard from '@/pages/Products/components/ProductCard.vue'
import { ContentType, DirectrEvent } from '@/services/directr'
import ChevronLeftIcon from '@/components/Icons/ChevronLeftIcon.vue'
import { UUID } from '@fusionauth/typescript-client'

export default defineComponent({
  name: 'ChannelOverview',
  props: {
    channelId: {
      type: String,
      required: false,
    },
  },
  components: {
    ChevronLeftIcon,
    ProductCard,
    TrashIcon,
    ChannelDetails,
    AddIcon,
    NavigationTabBarItem,
    NavigationTabBar,
    DButton,
    DButtonToggle,
    SelectHeader
  },
  setup (props) {
    const $store = useStore()
    const $q = useQuasar()
    const $directr = $store.getters['Services/Directr']
    const $router = useRouter()
    const $route = useRoute()

    const $userscope = computed(() => $store.getters['User/ScopeData'])
    const $userId = computed<UUID>(() => $store.getters['User/User']?.id)

    const fromProductId = $route.query.fromProduct
    const fromProduct = ref<CtCatalogueProduct | null>(null)

    const tabOptions = [
      { label: '', value: 'channel' },
      { label: '', value: 'client' },
    ]
    const toggleValue = ref('Channel')

    const state = reactive({
      activeTab: 'Channel',
      packing: [],
      storageTypes: [],
      entryConfig: computed(() => $store.getters['View/EntryConfig']),
      itemConfig: computed(() => $store.getters['View/Itemconfig']),
    })

    const catalogueModule = useContentModule<CtCatalogue>('Catalogues', $store)
    const { contentType, active, setActive, fetch, loading, mutate, sortOrder, requestHandler } = catalogueModule

    const catalogues = catalogueModule.items

    const setActiveCatalogue = ($event) => {
      if ($userscope.value?.channels && !$userscope.value?.channels.includes($event)) {
        if ($userscope.value?.channels?.length)
        return $router.push({ name: 'Channels', params: { channelId: $userscope.value?.channels[0] } })
      }

      const c = catalogues.value.find(c => c.sys.id === $event)
      if (!c) return
      setActive(c)
    }

    const setActiveCatalogueFromNav = async ($event) => {
      if ($userscope.value?.channels && !$userscope.value?.channels.includes($event))
        return

      return await $router.push({ name: 'Channels', params: { channelId: $event } })
    }

    const fetchQuery: Array<any> = []
    if ($userscope.value?.channels) fetchQuery.push({ field: 'sys.id', method: 'lin', match: $userscope.value?.channels })
    void fetch(fetchQuery, { limit: 1000 }).catch(console.error).then(() => {
      watch(() => props.channelId, (newValue) => {
        if ($userscope.value?.channels && !$userscope.value.channels.includes(props.channelId)) {
          return
        }

        if (newValue) {
          void setActiveCatalogue(newValue)
        } else if (catalogues.value?.length) {
          const firstCatalogueId = catalogues.value[0].sys.id
          void $router.push({ name: 'Channels', params: { channelId: firstCatalogueId } })
        }
      })
      watch(() => active.value, (next, prev) => {
        if (!next) return
        // console.log(next.sys.id !== props.channelId, next.sys.id, props.channelId)
        if (next.sys.id !== props.channelId) {
          void $router.push({ name: 'Channels', params: { channelId: next.sys.id } })
        }
      })

      if (props.channelId) {
        void setActiveCatalogue(props.channelId)
      } else if (catalogues.value?.length) {
        const firstCatalogueId = catalogues.value[0].sys.id
        void $router.replace({ name: 'Channels', params: { channelId: firstCatalogueId } })
      }
    })

    const onClientClick = () => {
      if (active.value?.client) {
        void $router.push({ name: 'Clients', params: { clientId: active.value.client, channelId: props.channelId } })
      } else {
        void $router.push({ name: 'Clients', params: { channelId: props.channelId } })
      }
    }

    requestHandler.get(
      'packing', [{ select: ['__id', 'name', 'id'] }]
    ).then(({ items = [] }) => state.packing = items).catch(console.error)

    requestHandler.get(
      'storage_type', [{ select: ['__id', 'id'] }]
    ).then(({ items = [] }) => state.storageTypes = items).catch(console.error)

    const saveCatalogue = async (sysId, savePayload) => {
      // console.log('saveCatalogue', sysId, savePayload)
      const response = await requestHandler.put(contentType, sysId, savePayload)
      mutate(response)
      void setActiveCatalogue(sysId)
    }

    const savePacking = () => {
      // pass
    }

    function createCatalogue (name) {
      if ($userscope.value?.channels) return

      const ids: Array<number> = catalogues.value.map(cat => parseInt(cat.id as any)).sort((a, b) => a - b)
      const id = ids[ids.length - 1] + 1
      return $directr.put('catalogue', [], { id, name }).then(({ items: [catalogue] }) => {
        mutate(catalogue, Mutation.create)

        active.value = catalogue
        $q.notify({ type: 'positive', classes: 'custom-notify success', message: 'New channel created' })
        return catalogue
      }).catch(e => {
        console.error(e)
        $q.notify({
          type: 'negative',
          message: 'Something went wrong wile creating the channel',
          classes: 'custom-notify error'
        })
      })
    }

    const addCatalogue = () => {
      $q.dialog({
        title: 'Create a new Channel',
        message: 'Please enter a channel name',
        prompt: {
          model: '',
          type: 'text',
          isValid: val => Boolean(val.length),
        },
        cancel: true,
      }).onOk((name) => {
        const ids: Array<number> = catalogues.value.map(cat => parseInt(cat.id as any)).sort((a, b) => a - b)
        const id = ids[ids.length - 1] + 1
        $directr.put('catalogue', [], { id, name }).then(({ items: [catalogue] }) => {
          mutate(catalogue, Mutation.create)

          setActive(catalogue)
        }).catch(console.error)
      }).onCancel(() => {
        // console.log('>>>> Cancel')
      }).onDismiss(() => {
        // console.log('I am triggered on both OK and Cancel')
      })
    }

    const deleteCatalogue = () => {
      const activeCatalogue = active.value
      if (!activeCatalogue) return
      $q.dialog({
        title: `Delete channel ${activeCatalogue?.name} (${activeCatalogue?.id})?`,
        message: 'Are you sure? This action cannot be undone',
        cancel: true,
        persistent: false
      }).onOk(() => {
        if (!activeCatalogue) return
        $directr.delete(activeCatalogue.sys.id).then(() => {
          if (!activeCatalogue) return
          const activeIndex: number = catalogues.value.findIndex(c => c?.sys?.id === activeCatalogue?.sys?.id)
          if (activeIndex + 1 < catalogues.value.length) {
            setActive(catalogues.value[activeIndex + 1])
          } else if (activeIndex > 0) {
            setActive(catalogues.value[activeIndex - 1])
          } else {
            active.value = null
          }
          mutate(activeCatalogue, Mutation.remove)
        })
      }).onCancel(() => {
        // console.log('>>>> Cancel')
      }).onDismiss(() => {
        // console.log('I am triggered on both OK and Cancel')
      })
    }

    onMounted(() => {
      if (fromProductId) {
        $directr.get(ContentType.Product, fromProductId).then(({ items }) => {
          if (!items.length) return

          fromProduct.value = items[0]
        })
      }
      if ($route.params.channelId) {
        void setActiveCatalogue($route.params.channelId)
      }
    })
    onMounted(() => {
      $directr.listen(DirectrEvent['Create/Catalogue'], (event) => {
        if (!event?.payload) return
        mutate(event?.payload, Mutation.create)
      })
      $directr.listen(DirectrEvent['Update/Catalogue'], (event) => {
        if (!event?.payload) return
        mutate(event?.payload, Mutation.update)
        $q.notify({ message: 'Catalogue updated', type: 'positive', classes: 'custom-notify success' })
      })
    })

    return {
      ...toRefs(state),
      userscope: $userscope,
      loading,
      active,
      setActive,
      setActiveCatalogue,
      setActiveCatalogueFromNav,
      saveCatalogue,
      savePacking,
      catalogues,
      mutate,
      Mutation,
      requestHandler,
      createCatalogue,
      addCatalogue,
      deleteCatalogue,
      onClientClick,
      notify: message => {
        $q.notify({ message, position: 'top', classes: 'custom-notify' })
      },
      tabOptions,
      toggleValue,
      fromProduct
    }
  }
})
